import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LogCategory,
  LogTemplateType,
  useKpiQaqcLogEntriesQuery,
  useAllQaqcLogsTemplatesForKpiTrackerQuery,
  useUpdateQaqcFormLogEntryKpiMutation,
  useUpsertLogEntryMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { COLLECTION_NAMES } from '@/constants';
import * as globalState from '@/components/Analysis/state';
import * as kpiState from '../state';
import { formatDateToYYYYMMDD } from '../utils';
import { buildQaqcFormValidationSchema } from '../QaQcPanel/qaqcFormValidation';
import {
  buildEmptyQaqcFormValues,
  formValuesToLogSectionInputs,
  mergeServerSectionsIntoForm,
  type QaqcFormValues,
} from '../QaQcPanel/utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { useJobPhaseRemarks } from './useJobPhaseRemarks';

export const useQaQcForm = () => {
  const structureId = useGetStructureId();
  const workpackId = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const unitSystem = useAtomValue(globalState.unitSystem);
  const setSelectedQaqcTemplate = useSetAtom(kpiState.selectedQaqcLogsTemplateForKpi);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formattedDate = useMemo(() => formatDateToYYYYMMDD(selectedDate), [selectedDate]);

  const shouldSkip = !structureId || !workpackId;

  const {
    data: templateData,
    loading: templateLoading,
    error: templateError,
  } = useAllQaqcLogsTemplatesForKpiTrackerQuery({
    variables: {
      input: {
        structureId: structureId ?? '',
        type: LogTemplateType.QaqcTemplate,
      },
    },
    skip: shouldSkip,
    fetchPolicy: 'cache-first',
  });

  const template = templateData?.allLogsTemplates?.at(0);

  useEffect(() => {
    if (template) {
      setSelectedQaqcTemplate(template);
    }
  }, [setSelectedQaqcTemplate, template]);

  const {
    data: logEntriesData,
    loading: logEntriesLoading,
    refetch: refetchQaqcLogEntries,
  } = useKpiQaqcLogEntriesQuery({
    variables: {
      input: {
        collectionName: COLLECTION_NAMES.WORKPACK,
        structureId: structureId ?? undefined,
        referenceId: workpackId,
        category: LogCategory.QaqcForm,
        logsTemplateId: template?.id,
        startDate: formattedDate,
        endDate: formattedDate,
        shift: selectedShift,
        timezone: userTimezone,
      },
    },
    skip: shouldSkip || !template?.id,
    fetchPolicy: 'cache-and-network',
  });

  const existingLog = useMemo(() => {
    const list = logEntriesData?.logEntries ?? [];
    if (!template?.id) {
      return undefined;
    }
    return list.find((entry) => entry.logsTemplate?.id === template.id) ?? list.at(0);
  }, [logEntriesData?.logEntries, template?.id]);

  const {
    remarksText,
    setRemarksText,
    remarksDirty,
    resetRemarksFromServer,
    persistJobPhaseRemarks,
    remarksPersistLoading,
  } = useJobPhaseRemarks({ logEntryId: existingLog?.id });

  const [upsertLogEntry, { loading: upsertLoading }] = useUpsertLogEntryMutation();
  const [updateQaqcForm, { loading: updateLoading }] = useUpdateQaqcFormLogEntryKpiMutation();

  const initialValues = useMemo(() => {
    const empty = buildEmptyQaqcFormValues(template);
    if (!existingLog?.sections?.length) {
      return empty;
    }
    return mergeServerSectionsIntoForm(empty, template, existingLog.sections, unitSystem);
  }, [existingLog?.sections, template, unitSystem]);

  const validationSchema = useMemo(() => buildQaqcFormValidationSchema(template), [template]);

  const { showSnackBar } = useSnackBarMessage({ atom: kpiState.kpiPanelSnackbarMessage });

  const qaqcSaveKindRef = useRef<'standard' | 'edited'>('standard');

  const formik = useFormik<QaqcFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!structureId || !workpackId || !template?.id) {
        return;
      }

      try {
        let logEntryId = existingLog?.id;

        if (!logEntryId) {
          const upsertResult = await upsertLogEntry({
            variables: {
              input: {
                collectionName: COLLECTION_NAMES.WORKPACK,
                structureId,
                referenceId: workpackId,
                category: LogCategory.QaqcForm,
                logsTemplateId: template.id,
                logDate: formattedDate,
                shift: selectedShift,
                fieldValues: [],
                lastModifiedFor: 'Submit job phase form',
              },
            },
          });
          logEntryId = upsertResult.data?.upsertLogEntry?.id;
        }

        if (!logEntryId) {
          showSnackBar('Could not create job phase log entry', 'error');
          return;
        }

        const sections = formValuesToLogSectionInputs(values, template, unitSystem);

        const lastModifiedFor =
          qaqcSaveKindRef.current === 'edited' ? 'Edit job phase form' : 'Submit job phase form';
        qaqcSaveKindRef.current = 'standard';

        await updateQaqcForm({
          variables: {
            input: {
              logEntryId,
              structureId,
              sections,
              lastModifiedFor,
            },
          },
        });

        await persistJobPhaseRemarks(logEntryId);

        showSnackBar('Job phase form saved successfully!', 'success');
        await refetchQaqcLogEntries();
      } catch (error) {
        const message = extractErrorMessageFromUnknownError(error);
        showSnackBar(message, 'error');
      }
    },
  });

  const isSubmitting = upsertLoading || updateLoading || remarksPersistLoading;

  const isQaqcPanelDirty = formik.dirty || remarksDirty;

  const submitWithSaveKind = useCallback(
    (kind: 'standard' | 'edited') => {
      qaqcSaveKindRef.current = kind;
      return formik.submitForm();
    },
    [formik]
  );

  return {
    formik,
    template,
    templateLoading,
    templateError,
    logEntriesLoading,
    isSubmitting,
    formattedDate,
    existingLog,
    submitWithSaveKind,
    remarksText,
    setRemarksText,
    resetRemarksFromServer,
    isQaqcPanelDirty,
  };
};
